import React, { useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import {
  Alert,
  Box,
  Button,
  Divider,
  FormControl,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  type SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@mui/material'
import HomeIcon from '@mui/icons-material/Home'
import SearchIcon from '@mui/icons-material/Search'
import { StartOutlined } from '@mui/icons-material'
import { DataGrid, type GridColumns } from '@mui/x-data-grid'

import { useSearchForAssetHolderQuery } from '../../../../api-services/asset-holdings/assetHolderApi'
import {
  useCreateInstructionMutation,
  useGetEnabledCurrenciesQuery
} from '../../../../api-services/financial/currencyExchangeApi'
import { useLedgerGetWalletsQuery } from '../../../../api-services/financial/ledgerAccountApi'
import { RtkQueryHelper } from '../../../../api-services/shared/RtkQueryHelper'
import { type AssetHoldersSearchRequest } from '../../../../api-schema-types/asset-holdings/AssetHolderPayloadDto'
import { type AssetHolderSummary } from '../../../../api-schema-types/asset-holdings/AssetHolderDomainDtos'
import { type LedgerAccountDetails } from '../../../../api-schema-types/financial/LedgerAccountDomainDtos'
import { useAppDispatch } from '../../../../app/hooks'
import { setNotification } from '../../../components/ui-widgets/uiWidgetsSlice'
import LoadingComponent from '../../../components/ui-widgets/LoadingComponent'
import ErrorComponent from '../../../components/ui-widgets/ErrorComponent'

const CreateInstruction: React.FC = () => {
  const history = useHistory()
  const dispatch = useAppDispatch()

  const goTo = (location: string): void => { history.push(location) }

  // Asset holder search / selection ==================================================================================

  const [searchQuery, setSearchQuery] = useState<AssetHoldersSearchRequest>({ searchInput: '' })
  const skipSearch = searchQuery.searchInput === ''
  const searchResult = useSearchForAssetHolderQuery(searchQuery, { skip: skipSearch })

  const [selectedAssetHolder, setSelectedAssetHolder] = useState<AssetHolderSummary | null>(null)

  const handleSearchQueryChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const input: string = event.target.value
    if (input.length >= 3 || input.length === 0) {
      setSearchQuery({ searchInput: input })
    }
  }

  // Currency selection ==============================================================================================

  const [fromCurrencyCode, setFromCurrencyCode] = useState<string>('')
  const [toCurrencyCode, setToCurrencyCode] = useState<string>('')

  const enabledCurrenciesResult = useGetEnabledCurrenciesQuery(undefined)

  // Validation (fetch balances before confirming) ==================================================================

  const [validated, setValidated] = useState<boolean>(false)

  const walletsResult = useLedgerGetWalletsQuery(
    { assetHolderId: selectedAssetHolder?.id },
    { skip: !validated || selectedAssetHolder === null }
  )

  // Reset the validated state whenever the inputs change so balances are always re-fetched.
  const resetValidation = (): void => { setValidated(false) }

  const handleSelectAssetHolder = (assetHolder: AssetHolderSummary): void => {
    setSelectedAssetHolder(assetHolder)
    resetValidation()
  }

  const handleFromCurrencyChange = (evt: SelectChangeEvent<string>): void => {
    setFromCurrencyCode(evt.target.value)
    resetValidation()
  }

  const handleToCurrencyChange = (evt: SelectChangeEvent<string>): void => {
    setToCurrencyCode(evt.target.value)
    resetValidation()
  }

  const sameCurrency = fromCurrencyCode !== '' && fromCurrencyCode === toCurrencyCode
  const canValidate = selectedAssetHolder !== null && fromCurrencyCode !== '' && toCurrencyCode !== '' && !sameCurrency

  const handleValidate = (): void => {
    if (canValidate) {
      setValidated(true)
    }
  }

  const fromWallet: LedgerAccountDetails | null =
    walletsResult.data?.find((w: LedgerAccountDetails) => w.currencyCode === fromCurrencyCode) ?? null

  // Confirm / create ===============================================================================================

  const [createInstructionTrigger, createInstructionResult] = useCreateInstructionMutation()

  const handleConfirm = async (): Promise<void> => {
    if (selectedAssetHolder === null) {
      return
    }
    await createInstructionTrigger({
      assetHolderId: selectedAssetHolder.id,
      fromCurrencyCode,
      toCurrencyCode
    })
  }

  useEffect(() => {
    if (createInstructionResult.isSuccess) {
      dispatch(setNotification({
        open: true,
        severity: 'success',
        message: `Currency exchange instruction ${createInstructionResult.data.currencyExchangeInstructionId} created.`
      }))
      // Reset the form for the next capture.
      setSelectedAssetHolder(null)
      setFromCurrencyCode('')
      setToCurrencyCode('')
      setValidated(false)
      setSearchQuery({ searchInput: '' })
    }

    if (createInstructionResult.isError) {
      dispatch(setNotification({
        open: true,
        severity: 'error',
        message: RtkQueryHelper.mapResponseError(createInstructionResult.error)
      }))
    }
  }, [createInstructionResult.isSuccess, createInstructionResult.isError])

  const balancesReady = validated && walletsResult.isSuccess
  const canConfirm = balancesReady && fromWallet !== null && fromWallet.availableBalance > 0 && !createInstructionResult.isLoading

  // =================================================================================================================

  const columns: GridColumns = [
    { field: 'id', headerName: 'Id', flex: 15 },
    {
      field: 'firstName',
      headerName: 'First Name',
      flex: 30,
      renderCell: params => (<Tooltip title={params.value}><p>{params.value}</p></Tooltip>)
    },
    {
      field: 'lastName',
      headerName: 'Last Name',
      flex: 30,
      renderCell: params => (<Tooltip title={params.value}><p>{params.value}</p></Tooltip>)
    },
    {
      field: 'username',
      headerName: 'Email Address',
      flex: 60,
      renderCell: params => (<Tooltip title={params.value}><p>{params.value}</p></Tooltip>)
    },
    {
      field: 'select',
      headerName: '',
      flex: 15,
      sortable: false,
      renderCell: params => (
        <Button variant="text" onClick={() => { handleSelectAssetHolder(params.row as AssetHolderSummary) }}>
          <StartOutlined />
        </Button>
      )
    }
  ]

  return (
    <>
      <Box sx={{ mb: 4, display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
        <Typography variant={'h4'}>Create Currency Exchange Instruction</Typography>
        <Button variant={'text'} type="button" onClick={() => { goTo('/app/finance-area') }}>
          <HomeIcon style={{ color: '#444' }} />
        </Button>
      </Box>

      <Paper>
        <Box sx={{ p: 4 }}>

          {/* Step 1: select asset holder ------------------------------------------------------------------------ */}
          <Typography variant={'h6'} sx={{ mb: 2 }}>1. Select asset holder</Typography>

          {selectedAssetHolder === null
            ? (
            <>
              <TextField
                label="Search by proxima id, first name, last name or email address."
                type={'search'}
                sx={{ width: '100%', mb: 2 }}
                InputProps={{
                  endAdornment: (<InputAdornment position="end"><SearchIcon /></InputAdornment>)
                }}
                onChange={handleSearchQueryChange}
              />
              {searchResult.isLoading && <LoadingComponent />}
              {(searchResult.error != null) && <ErrorComponent message={RtkQueryHelper.mapResponseError(searchResult.error)} />}
              {searchResult.isSuccess && (
                <DataGrid
                  rows={searchResult.data}
                  columns={columns}
                  autoHeight={true}
                  disableSelectionOnClick
                  pageSize={5}
                  rowsPerPageOptions={[5]}
                />
              )}
            </>
              )
            : (
            <Alert
              severity="info"
              action={<Button color="inherit" size="small" onClick={() => { setSelectedAssetHolder(null); resetValidation() }}>Change</Button>}
            >
              Selected: <strong>{selectedAssetHolder.firstName} {selectedAssetHolder.lastName}</strong> ({selectedAssetHolder.username}) — id {selectedAssetHolder.id}
            </Alert>
              )}

          <Divider sx={{ my: 4 }} />

          {/* Step 2: select currencies -------------------------------------------------------------------------- */}
          <Typography variant={'h6'} sx={{ mb: 2 }}>2. Select conversion pair</Typography>

          {enabledCurrenciesResult.isLoading && <LoadingComponent />}
          {(enabledCurrenciesResult.error != null) && <ErrorComponent message={RtkQueryHelper.mapResponseError(enabledCurrenciesResult.error)} />}

          <Grid container spacing={2} alignItems={'center'}>
            <Grid item md={3}>
              <FormControl sx={{ width: '100%' }}>
                <InputLabel>From Currency</InputLabel>
                <Select
                  label={'From Currency'}
                  disabled={selectedAssetHolder === null || !enabledCurrenciesResult.isSuccess}
                  onChange={handleFromCurrencyChange}
                  value={fromCurrencyCode}
                >
                  {enabledCurrenciesResult.data?.map((c) => (
                    <MenuItem key={c.code} value={c.code}>{c.code} — {c.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item md={3}>
              <FormControl sx={{ width: '100%' }}>
                <InputLabel>To Currency</InputLabel>
                <Select
                  label={'To Currency'}
                  disabled={selectedAssetHolder === null || !enabledCurrenciesResult.isSuccess}
                  onChange={handleToCurrencyChange}
                  value={toCurrencyCode}
                >
                  {enabledCurrenciesResult.data?.map((c) => (
                    <MenuItem key={c.code} value={c.code}>{c.code} — {c.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item md={3}>
              <Button
                type={'button'}
                variant={'outlined'}
                disabled={!canValidate}
                onClick={handleValidate}
              >
                Validate
              </Button>
            </Grid>
          </Grid>

          {sameCurrency && (
            <Alert severity="warning" sx={{ mt: 2 }}>From and To currencies must be different.</Alert>
          )}

          <Divider sx={{ my: 4 }} />

          {/* Step 3: review balances + confirm ------------------------------------------------------------------ */}
          <Typography variant={'h6'} sx={{ mb: 2 }}>3. Review balance &amp; confirm</Typography>

          {!validated && (
            <Typography variant={'body2'} color={'text.secondary'}>
              Select an asset holder and conversion pair, then press <strong>Validate</strong> to load the balance.
            </Typography>
          )}

          {validated && walletsResult.isLoading && <LoadingComponent />}
          {validated && (walletsResult.error != null) && <ErrorComponent message={RtkQueryHelper.mapResponseError(walletsResult.error)} />}

          {balancesReady && (
            <>
              {fromWallet === null
                ? (
                <Alert severity="error" sx={{ mb: 2 }}>
                  This asset holder has no <strong>{fromCurrencyCode}</strong> wallet, so no funds can be converted.
                </Alert>
                  )
                : fromWallet.availableBalance <= 0
                  ? (
                <Alert severity="error" sx={{ mb: 2 }}>
                  The <strong>{fromCurrencyCode}</strong> wallet has no available balance to convert.
                </Alert>
                    )
                  : (
                <Alert severity="success" sx={{ mb: 2 }}>
                  The entire available <strong>{fromCurrencyCode}</strong> balance of{' '}
                  <strong>{fromWallet.availableBalanceFormatted}</strong> will be converted to <strong>{toCurrencyCode}</strong>.
                </Alert>
                    )}

              <Table size="small" sx={{ mb: 3, maxWidth: 640 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Currency</TableCell>
                    <TableCell align="right">Available</TableCell>
                    <TableCell align="right">Current</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(walletsResult.data ?? []).map((w: LedgerAccountDetails) => (
                    <TableRow
                      key={w.ledgerAccountId}
                      selected={w.currencyCode === fromCurrencyCode}
                    >
                      <TableCell>{w.currencyCode}</TableCell>
                      <TableCell align="right">{w.availableBalanceFormatted}</TableCell>
                      <TableCell align="right">{w.currentBalanceFormatted}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              <Button
                type={'button'}
                variant={'contained'}
                disabled={!canConfirm}
                onClick={handleConfirm}
              >
                Confirm &amp; Create Instruction
              </Button>
            </>
          )}

        </Box>
      </Paper>
    </>
  )
}

export default CreateInstruction
